// ============================================================================
// lp-identity.js — LP Identity Persistence (Workstream D Component 3)
// ----------------------------------------------------------------------------
// WHAT IT DOES:
//   On DOMContentLoaded:
//     1. Reads the data-lp-variant attribute on <body> (populated by lp.liquid
//        from the URL's lp_test_id query parameter).
//     2. Writes a 1st-party _lp_variant cookie (max-age 7 days, SameSite Lax)
//        so the value survives a same-session SPA navigation or short refresh.
//     3. Collects data-lp-snippet handles from the rendered DOM and FILTERS
//        them against the active-test allowlist (the <meta name="lp-active-snippets">
//        content). Snippets outside the active test are not reported.
//     4. Pushes one event to window.ElevarDataLayer:
//          { event: 'lp_identity',
//            lp_test_id: <test id>,
//            lp_snippets_seen: '<pipe|delimited|list>' }
//        Elevar's existing order-note-attribute hook writes both keys onto the
//        Shopify order at first checkout. Skio copies them onto renewals.
//        Klaviyo receives them as Session Enrichment profile properties.
//
// WHY:
//   Spine S5 identity persistence — LP identity rides the existing Elevar →
//   Shopify order-note → Skio renewal → Klaviyo Session Enrichment pipeline.
//   We ADD note-attribute keys; we DO NOT modify the _elevar_visitor_info
//   carrier mechanics (NT-4). Klaviyo is downstream only — this file writes
//   to window.ElevarDataLayer and NEVER to window._learnq (brief Decision 3).
//
// WHEN TO CHANGE:
//   - The allowlist source moves from a meta tag to an endpoint → swap the
//     allowlist read.
//   - The order-note key names change (Workstream D config.yml) → update the
//     two keys pushed into the data-layer event.
//   - The snippet ID format changes → update the validation regex below.
//
// DEPENDENCIES:
//   - data-lp-variant attribute on <body> (lp.liquid)
//   - <meta name="lp-active-snippets"> (rendered by sections at Workstream
//     C's binding point; empty meta == no active snippets to report)
//   - window.ElevarDataLayer (Elevar's data-layer queue, owned by the TAE)
// ============================================================================

(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Snippet ID validation. The format ADR-2 froze: s_<module>_<6hex>.
  // Anything that doesn't match is dropped (defends against a bad section
  // emitting garbage that would pollute the order note).
  var SNIPPET_ID_RE = /^s_[a-z0-9-]+_[a-f0-9]{6}$/;

  function readAllowlist() {
    var meta = document.querySelector('meta[name="lp-active-snippets"]');
    if (!meta) return [];
    var raw = meta.getAttribute('content') || '';
    var parts = raw.split('|');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var v = (parts[i] || '').trim();
      // Only allow snippet ids that match the contract regex.
      if (v && SNIPPET_ID_RE.test(v)) out.push(v);
    }
    return out;
  }

  function collectSeenSnippets(allowlist) {
    if (!allowlist.length) return [];
    var nodes = document.querySelectorAll('[data-lp-snippet]');
    var seen = [];
    var allowSet = {};
    for (var i = 0; i < allowlist.length; i++) {
      allowSet[allowlist[i]] = true;
    }
    for (var n = 0; n < nodes.length; n++) {
      var id = (nodes[n].getAttribute('data-lp-snippet') || '').trim();
      // Allowlist-bounded AND format-valid AND not already collected.
      if (id && allowSet[id] === true && seen.indexOf(id) === -1) {
        seen.push(id);
      }
    }
    return seen;
  }

  function run() {
    var body = document.body;
    if (!body || !body.dataset) return;

    var lpTestId = body.dataset.lpVariant || '';
    if (!lpTestId) return;  // No LP test on this visit — no-op.

    // 1st-party JS cookie (Safari ITP caps it at 7 days; that is documented
    // in the brief Decision 3 and accepted in ADR-3). path=/ so the cookie
    // survives client-side route changes; SameSite=Lax so the cookie is sent
    // on top-level checkout navigations.
    var maxAgeSeconds = 7 * 24 * 60 * 60;
    document.cookie = '_lp_variant=' + encodeURIComponent(lpTestId)
      + '; max-age=' + maxAgeSeconds + '; path=/; SameSite=Lax';

    // Active-test allowlist + filtered seen-list. Pipe delimiter is ADR-2.
    var allowlist = readAllowlist();
    var seen = collectSeenSnippets(allowlist);

    // Push to Elevar's data layer. Elevar writes both as Shopify order note
    // attributes at first checkout. Note: this file NEVER writes to
    // window._learnq — Klaviyo is downstream only (Decision 3).
    window.ElevarDataLayer = window.ElevarDataLayer || [];
    window.ElevarDataLayer.push({
      event: 'lp_identity',
      lp_test_id: lpTestId,
      lp_snippets_seen: seen.join('|'),
    });
  }

  // Run on DOMContentLoaded so the DOM is parsed and section-emitted
  // data-lp-snippet attributes are present. If we're already past
  // DOMContentLoaded (defer attribute happens to land after it), run
  // synchronously.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
